/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useNetworkLoader from 'hooks/useNetworkLoader';
import { getAnnoncementData } from 'Components/Home/HomeServices';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';


/* ── Per-type gradient + badge config ───────────────────────── */
const BANNER_TYPE_CFG = {
  INFORMATION: { from: '#a9bfd3', to: '#007BC6', label: 'Information', fallback: 'linear-gradient(135deg, #0F4C75 0%, #1B262C 50%, #3282B8 100%)', overlayOpacity: 0.78 },
  PROMOTION:   { from: '#1A3F6F', to: '#007BC6', label: 'Promotion',  fallback: 'linear-gradient(135deg, #1A3F6F 0%, #1B262C 50%, #2B5EA6 100%)', overlayOpacity: 0.55 },
  NOUVEAUTE:   { from: '#0000FF80', to: '#0d5530', label: 'Nouveauté',  fallback: 'linear-gradient(135deg, #0d5530 0%, #1B3A4B 50%, #2E86AB 100%)', overlayOpacity: 0.78 },
  EVENT:       { from: '#0000FF80', to: '#06304A', label: 'Evénement/Autres', fallback: 'linear-gradient(135deg, #1A3C5E 0%, #0F2D44 50%, #2574A9 100%)', overlayOpacity: 0.78 },
};

const formatDate = (d) => {
  try { return format(new Date(d.replace('+0000', '+0100')), 'dd MMM yyyy', { locale: fr }); }
  catch { return ''; }
};

/* ── AnnouncementBanner ──────────────────────────────────────── */
const AnnouncementBanner = () => {
  const { callApi } = useNetworkLoader();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [interactionKey, setInteractionKey] = useState(0);
  const preloadRef = React.useRef([]);

  useEffect(() => {
    callApi(
      () => getAnnoncementData(0),
      (data) => {
        const list = data?.content || [];
        setAnnouncements(list);
        setLoading(false);
        // Keep Image objects in a ref so the GC can't abort in-flight downloads
        preloadRef.current = list.map((ann) => {
          const src = (ann.image ? ann.imageUrl : null) || (ann.imageEvent ? ann.imageEventUrl : null);
          if (!src) return null;
          const img = new Image();
          img.src = src;
          return img;
        }).filter(Boolean);
      },
      false,
    );
  }, []);

  useEffect(() => {
    if (announcements.length <= 1) return undefined;
    const id = setInterval(() => setIndex((p) => (p + 1) % announcements.length), 5000);
    return () => clearInterval(id);
  }, [announcements.length, interactionKey]);

  const goTo = (i) => {
    setIndex(i);
    setInteractionKey((k) => k + 1);
  };

  if (loading) return null;

  if (!announcements.length) return null;
  const ann = announcements[index];
  if (!ann) return null;

  const cfg = BANNER_TYPE_CFG[ann.type] || BANNER_TYPE_CFG.INFORMATION;
  const annLink = ann.linkEvent || '';
  const annFile = ann.fileEventUrl || '';
  const annFileName = 'Fichier joint';
  const imgSrc = (ann.image ? ann.imageUrl : null) || (ann.imageEvent ? ann.imageEventUrl : null) || null;
  const annDescription = ann.descriptionEvent || ann.description || '';

  return (
    <Box
      sx={{
        mx: '40px',
        mt: '20px',
        mb: '0px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        p: '8px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '251px',
          borderRadius: '4px',
          overflow: 'hidden',
          background: imgSrc ? cfg.to : cfg.fallback,
        }}
      >
        {/* Background image — admin-provided only */}
        {imgSrc && (
          <>
            <Box
              component="img"
              key={imgSrc}
              src={imgSrc}
              alt=""
              ref={(el) => { if (el && el.complete && el.naturalWidth > 0) el.style.opacity = 1; }}
              onLoad={(e) => { e.currentTarget.style.opacity = 1; }}
              sx={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'right center',
                display: 'block',
                opacity: 0,
                transition: 'opacity 0.35s ease',
              }}
            />
            {/* Dark gradient overlay (bottom) */}
            <Box sx={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(182deg, #06304A00 0%, #06304A 100%)`,
              opacity: cfg.overlayOpacity || 0.78,
            }} />
            {/* Per-type colour tint (left) */}
            <Box sx={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(to right, ${cfg.from}EE 0%, ${cfg.from}99 40%, transparent 70%)`,
            }} />
          </>
        )}

        {/* ── Nav arrows ── */}
        {announcements.length > 1 && (
          <>
            <Box
              onClick={(e) => { e.stopPropagation(); goTo((index - 1 + announcements.length) % announcements.length); }}
              sx={{
                position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                zIndex: 2, width: 36, height: 36, borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.30)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', backdropFilter: 'blur(4px)',
                transition: 'background 0.2s',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
              }}
            >
              <ChevronLeftIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box
              onClick={(e) => { e.stopPropagation(); goTo((index + 1) % announcements.length); }}
              sx={{
                position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                zIndex: 2, width: 36, height: 36, borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.30)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', backdropFilter: 'blur(4px)',
                transition: 'background 0.2s',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
              }}
            >
              <ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
          </>
        )}

        {/* ── Content ── */}
        <Box sx={{
          position: 'relative', zIndex: 1,
          height: '100%',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-start',
          px: { xs: '16px', md: '24px' },
          py: '18px',
        }}>
          {/* Type badge */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start',
            backgroundColor: 'rgba(255, 255, 255, 0.14)', color: '#FFFFFF',
            fontSize: '13px', fontWeight: 700, fontFamily: 'Open Sans, sans-serif',
            lineHeight: '14px',
            px: '12px', py: '6px',
            borderRadius: '4px', letterSpacing: '0.3px',
          }}>
            {cfg.label}
          </Box>

          {/* Fixed gap below badge — dots always same distance from badge */}
          <Box sx={{ height: '14px', flexShrink: 0 }} />

          {/* Carousel dots */}
          {announcements.length > 1 && (
            <Box sx={{ display: 'flex', gap: '5px' }}>
              {announcements.map((_, i) => (
                <Box
                  key={i}
                  onClick={(e) => { e.stopPropagation(); goTo(i); }}
                  sx={{
                    width: i === index ? '22px' : '6px', height: '4px',
                    borderRadius: '2px', cursor: 'pointer',
                    backgroundColor: i === index ? '#fff' : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.5s ease',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Flexible spacer — pushes title to the bottom */}
          <Box sx={{ flex: 1 }} />

          {/* Bottom row: title + description left / date + link right */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
            {/* Left */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                color: '#FFFFFF', fontWeight: 700,
                fontSize: { xs: '16px', md: '22px' },
                lineHeight: 1.3, mb: '4px',
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>
                {ann.title}
              </Typography>
              {annDescription && (
                ann.type === 'EVENT' ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: annDescription }}
                    sx={{
                      color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, lineHeight: 1.5,
                      overflow: 'hidden', display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      '& *': { color: 'rgba(255,255,255,0.85)', margin: 0 },
                    }}
                  />
                ) : (
                  <Typography sx={{
                    color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, lineHeight: 1.5,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {annDescription}
                  </Typography>
                )
              )}
            </Box>

            {/* Right: date + link + file + CTA */}
            {(ann.startDate || ann.endDate || annLink || annFile || ann.type === 'PROMOTION' || ann.type === 'NOUVEAUTE') && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                {(ann.startDate || ann.endDate) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                    <Typography sx={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 500 }}>
                      {ann.startDate && ann.endDate
                        ? `${formatDate(ann.startDate)} – ${formatDate(ann.endDate)}`
                        : formatDate(ann.endDate || ann.startDate)}
                    </Typography>
                  </Box>
                )}
                {(annLink || annFile) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {annLink && (
                      <Typography
                        component="a"
                        href={annLink}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: 'rgba(255,255,255,0.75)', fontSize: '12px',
                          textDecoration: 'underline',
                          '&:hover': { color: '#fff' },
                        }}
                      >
                        {annLink}
                      </Typography>
                    )}
                    {annFile && (
                      <Box
                        component="a"
                        href={annFile}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          color: '#FFFFFF',
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          border: '1px solid rgba(255,255,255,0.35)',
                          borderRadius: '6px',
                          px: '12px', py: '6px',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 15, color: '#FFFFFF' }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#FFFFFF' }}>
                          {annFileName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AnnouncementBanner;
